import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { AutomationDateRuleTypes, AutomationEventTypes, AutomationTypes } from '@tokeet-frontend/automations'
import { UntypedFormBuilder } from '@angular/forms'
import { Store } from '@ngrx/store'
import { untilDestroy } from '@tokeet-frontend/tv3-platform'
import { guestBookingNotificationMessages, userBookingNotificationMessages } from './messages'
import { AutomationWizardPanelBaseComponent } from '../automation-wizard-panel.component'
import moment from 'moment/moment'
import { AuthService } from '@tv3/services/auth.service'
import { Actions } from '@ngrx/effects'
import * as lodash from 'lodash'
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators'

@Component({
  selector: 'app-booking-automation-wizard',
  templateUrl: './booking-automation-wizard.component.html',
  styleUrls: ['./booking-automation-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BookingAutomationWizardComponent extends AutomationWizardPanelBaseComponent implements OnInit {
  type = AutomationTypes.Booking

  guestNotificationMessages = guestBookingNotificationMessages
  userNotificationMessages = userBookingNotificationMessages

  taskBeforeCheckout = {
    name: $localize`Schedule any necessary services before checkout`,
    user_ids: [this.auth.user.id],
    start: {
      base: AutomationDateRuleTypes.BeforeCheckOut,
      days: 0,
      hours: 2,
    },
    due: {
      base: AutomationDateRuleTypes.BeforeCheckOut,
      days: 0,
      hours: 0,
    },
    notes:
      'Guest is being checked in, please take any necessary actions that are required once the guest is checked in',
    timezone: moment.tz.guess(),
  }

  taskAfterCheckout = {
    name: $localize`Schedule follow-up actions after checkout`,
    user_ids: [this.auth.user.id],
    start: {
      base: AutomationDateRuleTypes.AfterCheckOut,
      days: 0,
      hours: 0,
    },
    due: {
      base: AutomationDateRuleTypes.AfterCheckOut,
      days: 0,
      hours: 2,
    },
    notes:
      'Guest is being checked out, please take any necessary actions that are required once the guest is checked out',
    timezone: moment.tz.guess(),
  }

  taskAfterRentalChanged = {
    name: $localize`Schedule any necessary services after rental changed`,
    user_ids: [this.auth.user.id],
    start: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 1,
      hours: 0,
    },
    due: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 0,
      hours: 0,
    },
    notes: 'Booking rental is changed, please take any necessary actions that are required once the rental is changed',
    timezone: moment.tz.guess(),
  }

  taskAfterDatesChanged = {
    name: $localize`Schedule any necessary services after dates changed`,
    user_ids: [this.auth.user.id],
    start: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 1,
      hours: 0,
    },
    due: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 0,
      hours: 0,
    },
    notes: 'Booking dates are changed, please take any necessary actions that are required once the dates are changed',
    timezone: moment.tz.guess(),
  }

  taskAfterPaid = {
    name: $localize`Schedule any necessary services after booking paid`,
    user_ids: [this.auth.user.id],
    start: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 1,
      hours: 0,
    },
    due: {
      base: AutomationDateRuleTypes.BeforeCheckIn,
      days: 0,
      hours: 0,
    },
    notes: 'Booking is being paid, please take any necessary actions that are required once the booking is paid',
    timezone: moment.tz.guess(),
  }

  constructor(
    private auth: AuthService,
    protected actions: Actions,
    protected fb: UntypedFormBuilder,
    protected store: Store<any>
  ) {
    super(actions, store, fb)
    this.eventCtrl.setValue(AutomationEventTypes.InquiryCreated)
  }

  ngOnInit(): void {
    super.ngOnInit()

    this.automations$
      .pipe(
        map((automations) => {
          let automation = lodash.head(automations)
          if (this.eventCtrl.value === AutomationEventTypes.TimeBeforeBookingCheckIn) {
            const excludeIdSuffix = ['send-guest-welcome', 'send-guest-checkin-instructions']
            automation = automations.find(
              (t) => !excludeIdSuffix.find((s) => lodash.endsWith(t.attributes?.autopilot, s))
            )
          } else if (this.eventCtrl.value === AutomationEventTypes.TimeAfterBookingCheckout) {
            const excludeIdSuffix = ['send-guest-review']
            automation = automations.find(
              (t) => !excludeIdSuffix.find((s) => lodash.endsWith(t.attributes?.autopilot, s))
            )
          }
          return automation
        }),
        filter((automation) => !!automation),
        distinctUntilChanged((a, b) => a?.event === b?.event),
        untilDestroy(this)
      )
      .subscribe((automation) => {
        this.form.patchValue({
          days: automation?.settings?.days,
          hours: automation?.settings?.hours,
        })
      })
  }
}
